import { useCallback } from 'react';

import { Callout } from '@/_shared/components/ui/callout';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';

import { SettingsSectionHeader } from '../components/SettingsSectionHeader';
import { SlackConfirmDialog } from '../components/slack/SlackConfirmDialog';
import { SlackConnectedChannelsSection } from '../components/slack/SlackConnectedChannelsSection';
import { SlackConnectedUsersSection } from '../components/slack/SlackConnectedUsersSection';
import { SlackConnectionCard } from '../components/slack/SlackConnectionCard';
import { SlackConnectPrompt } from '../components/slack/SlackConnectPrompt';
import { SlackSectionSkeleton } from '../components/slack/SlackSkeletons';
import { useSlackIntegration } from '../components/slack/useSlackIntegration';
import { useSlackOAuthResultToast } from '../components/slack/useSlackOAuthResultToast';
import { useSlackTableColumns } from '../components/slack/useSlackTableColumns';

/**
 * Slack integration — rendered as a section inside the Integrations settings tab,
 * not as a standalone page. Members get read-only access; admins can connect,
 * uninstall, and manage links/bindings.
 */
export function SlackIntegrationSection(): React.ReactElement {
  const { isAdmin, workspace } = useWorkspace();
  // Toast the Slack OAuth install result the callback redirected here with (`?success`/`?error`).
  useSlackOAuthResultToast();
  const {
    statusQuery,
    linksQuery,
    bindingsQuery,
    settingsQuery,
    slackWorkspaceBaseUrl,
    guestAccessMutation,
    uninstallMutation,
    pending,
    setPending,
    actionPending,
    confirmAction,
    pendingConfig,
  } = useSlackIntegration();

  const { linkColumns, bindingColumns } = useSlackTableColumns({
    isAdmin,
    slackWorkspaceBaseUrl,
    onPendingAction: setPending,
  });

  const handleUninstall = useCallback(() => {
    const data = statusQuery.data;
    if (data?.installed && data.teamId) {
      uninstallMutation.mutate({ teamId: data.teamId });
    }
  }, [statusQuery.data, uninstallMutation]);

  const status = statusQuery.data;
  const installed = !!status?.installed;
  const notConfigured = !!status && 'configured' in status && !status.configured;

  let body: React.ReactNode;

  if (statusQuery.isLoading) {
    body = <SlackSectionSkeleton />;
  } else if (statusQuery.isError) {
    body = (
      <Callout variant="error">
        <p className="font-medium">Failed to load Slack status</p>
        <p className="mt-1 text-xs opacity-80">Please try refreshing the page.</p>
      </Callout>
    );
  } else if (notConfigured) {
    body = (
      <Callout variant="warning">
        <p className="font-medium">Slack integration is not configured</p>
        <p className="mt-1 text-xs opacity-80">
          The server is missing required Slack environment variables (SLACK_CLIENT_ID, SLACK_CLIENT_SECRET,
          SLACK_SIGNING_SECRET, SLACK_APP_ID). Contact the administrator to configure the Slack app.
        </p>
      </Callout>
    );
  } else if (!installed) {
    body = <SlackConnectPrompt isAdmin={isAdmin} workspaceId={workspace.id} />;
  } else {
    const links = linksQuery.data ?? [];
    const bindings = bindingsQuery.data ?? [];

    body = (
      <div className="space-y-6">
        <SlackConnectionCard
          teamName={status.teamName}
          teamUrl={status.teamUrl}
          connectedUserCount={links.length}
          connectedChannelCount={bindings.length}
          isAdmin={isAdmin}
          settingsLoaded={!!settingsQuery.data?.settings}
          guestAccessEnabled={settingsQuery.data?.settings.slackGuestAccessEnabled ?? false}
          guestAccessDisabled={!isAdmin || guestAccessMutation.isPending || settingsQuery.isLoading}
          onGuestAccessChange={(checked) => guestAccessMutation.mutate({ slackGuestAccessEnabled: checked })}
          uninstallPending={uninstallMutation.isPending}
          onUninstall={handleUninstall}
        />
        <SlackConnectedUsersSection
          count={links.length}
          links={links}
          columns={linkColumns}
          isLoading={linksQuery.isLoading}
          isError={linksQuery.isError}
        />
        <SlackConnectedChannelsSection
          count={bindings.length}
          bindings={bindings}
          columns={bindingColumns}
          isLoading={bindingsQuery.isLoading}
          isError={bindingsQuery.isError}
        />
      </div>
    );
  }

  return (
    <section>
      <SettingsSectionHeader
        title="Slack"
        description="Chat with Lookout from Slack — DM the bot or @mention it in a channel to ask about your data."
      />
      {body}
      <SlackConfirmDialog
        pending={pending}
        title={pendingConfig?.title}
        description={pendingConfig?.describe(pending?.name ?? '')}
        confirmLabel={pendingConfig?.confirmLabel}
        actionPending={actionPending}
        onClose={() => setPending(null)}
        onConfirm={confirmAction}
      />
    </section>
  );
}
